import { useEffect, useMemo, useState } from 'react';
import { getArticleCoverImages, resolveMediaUrl } from '../../lib/utils';

export default function ArticleCoverMedia({
  article,
  src = '',
  alt = '',
  fallbackSrc = '',
  className = '',
  imageClassName = '',
  loading = 'lazy',
  fetchPriority,
  decoding = 'async',
  objectPosition = 'top center',
  maxImages = 1,
  onEmpty,
}) {
  const primarySources = useMemo(() => {
    const baseSources = src ? [resolveMediaUrl(src)] : getArticleCoverImages(article);
    const seen = new Set();
    const safeMaxImages = Math.max(1, Math.min(2, Number(maxImages) || 1));

    return baseSources
      .filter(Boolean)
      .filter((image) => {
        if (seen.has(image)) return false;
        seen.add(image);
        return true;
      })
      .slice(0, safeMaxImages);
  }, [article, maxImages, src]);
  const fallbackSource = resolveMediaUrl(fallbackSrc);
  const sourceKey = [...primarySources, fallbackSource].join('|');
  const [failedImages, setFailedImages] = useState({});
  const [fallbackFailed, setFallbackFailed] = useState(false);

  useEffect(() => {
    setFailedImages({});
    setFallbackFailed(false);
  }, [sourceKey]);

  const visiblePrimarySources = primarySources.filter((image) => !failedImages[image]);
  const visibleSources = visiblePrimarySources.length
    ? visiblePrimarySources
    : (fallbackSource && !fallbackFailed ? [fallbackSource] : []);

  useEffect(() => {
    if ((!primarySources.length && !fallbackSource) || visibleSources.length) return;
    onEmpty?.();
  }, [fallbackSource, onEmpty, primarySources.length, visibleSources.length]);

  if (!visibleSources.length) return null;

  const wrapperClassName = [
    className,
    visibleSources.length > 1 ? 'grid grid-cols-2' : '',
  ].filter(Boolean).join(' ');
  const priorityProps = fetchPriority ? { fetchpriority: fetchPriority } : {};

  return (
    <div className={wrapperClassName}>
      {visibleSources.map((image, index) => (
        <div key={image} className="h-full w-full min-w-0 overflow-hidden">
          <img
            src={image}
            alt={index === 0 ? alt : ''}
            className={`w-full h-full object-cover ${imageClassName}`.trim()}
            style={{ objectPosition }}
            loading={loading}
            decoding={decoding}
            onError={() => {
              if (image === fallbackSource) {
                setFallbackFailed(true);
                return;
              }
              setFailedImages((current) => ({ ...current, [image]: true }));
            }}
            {...priorityProps}
          />
        </div>
      ))}
    </div>
  );
}
